import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronRight, faChevronLeft, faCheck,
    faEnvelope, faComment, faMobileAlt, faPaperPlane, faTimes,
    faUsers, faStar, faClock, faUserPlus, faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import './CampaignCreateModal.scss';
import { API_BASE_URL } from '../../api';

const CANAL_OPTIONS = [
    { value: 'email',    label: 'Email',    icon: faEnvelope,  desc: 'Campagne email avec mise en page riche' },
    { value: 'whatsapp', label: 'WhatsApp', icon: faComment,   desc: 'Message WhatsApp personnalisé' },
    { value: 'sms',      label: 'SMS',      icon: faMobileAlt, desc: 'SMS court, idéal pour les promotions flash' },
];

const STEPS = ['Paramètres', 'Audience', 'Confirmation'];

const SEGMENT_DEFS = [
    { key: 'tous',      label: 'Tous les patients', icon: faUsers,    color: '#2563eb', desc: 'Tous les patients enregistrés' },
    { key: 'fideles',   label: 'Clients fidèles',   icon: faStar,     color: '#f59e0b', desc: '10 ventes ou plus' },
    { key: 'inactifs',  label: 'Clients inactifs',  icon: faClock,    color: '#6b7280', desc: 'Aucun achat depuis 60 jours' },
    { key: 'nouveaux',  label: 'Nouveaux clients',  icon: faUserPlus, color: '#10b981', desc: 'Premier achat dans les 30 derniers jours' },
];

export default function CampaignCreateModal({ onClose, onCreated }) {
    const navigate = useNavigate();

    const [step, setStep] = useState(0);
    const [saving, setSaving] = useState(false);

    // Step 1
    const [nom, setNom] = useState('');
    const [canal, setCanal] = useState('email');
    const [templateId, setTemplateId] = useState('');
    const [sujet, setSujet] = useState('');
    const [templates, setTemplates] = useState([]);

    // Step 2 — audience
    const [audienceMode, setAudienceMode] = useState('segment'); // 'segment' | 'manuel'
    const [segmentCounts, setSegmentCounts] = useState(null);
    const [selectedSegment, setSelectedSegment] = useState('tous');
    const [patients, setPatients] = useState([]);
    const [patientsLoading, setPatientsLoading] = useState(false);
    const [searchQ, setSearchQ] = useState('');
    const [selectedPatientIds, setSelectedPatientIds] = useState([]);

    // Step 3
    const [dateEnvoi, setDateEnvoi] = useState('');
    const [contenu, setContenu] = useState('');
    const [offre, setOffre] = useState('');

    // ── Init ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/v1/marketing/templates`, { withCredentials: true })
            .then(({ data }) => setTemplates(data))
            .catch(console.error);

        axios.get(`${API_BASE_URL}/api/v1/marketing/segments/counts`, { withCredentials: true })
            .then(({ data }) => setSegmentCounts(data))
            .catch(console.error);
    }, []);

    useEffect(() => {
        const handler = e => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose]);

    // ── Patient search (manual mode) ─────────────────────────────────────────
    const loadPatients = useCallback((q) => {
        setPatientsLoading(true);
        axios.get(`${API_BASE_URL}/api/v1/marketing/segments/patients`, {
            params: { q },
            withCredentials: true,
        }).then(({ data }) => setPatients(data))
          .catch(console.error)
          .finally(() => setPatientsLoading(false));
    }, []);

    useEffect(() => {
        if (audienceMode !== 'manuel') return;
        const t = setTimeout(() => loadPatients(searchQ), 300);
        return () => clearTimeout(t);
    }, [searchQ, audienceMode, loadPatients]);

    useEffect(() => {
        if (audienceMode === 'manuel' && patients.length === 0 && !patientsLoading) {
            loadPatients('');
        }
    }, [audienceMode]);

    // ── Derived values ───────────────────────────────────────────────────────
    const selectedTemplate = templates.find(t => String(t.id) === String(templateId));
    const filteredTemplates = templates.filter(t => t.canal === canal);

    const audienceCount = audienceMode === 'segment'
        ? (segmentCounts ? (segmentCounts[selectedSegment] ?? 0) : 0)
        : selectedPatientIds.length;

    const canNext = () => {
        if (step === 0) return nom.trim().length > 0;
        if (step === 1) return audienceCount > 0;
        return true;
    };

    // ── Manual selection helpers ─────────────────────────────────────────────
    const togglePatient = (id) => {
        setSelectedPatientIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const selectAll = () => setSelectedPatientIds(patients.map(p => p.id));
    const deselectAll = () => setSelectedPatientIds([]);

    // ── Submit ───────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        setSaving(true);
        try {
            const payload = {
                nom,
                canal,
                templateId: templateId ? Number(templateId) : null,
                sujet: sujet || null,
                contenuPersonnalise: contenu || null,
                offre: offre || null,
                segment: audienceMode === 'segment' ? selectedSegment : 'manuel',
                destinataires: audienceCount,
                patientIds: audienceMode === 'manuel' ? selectedPatientIds : [],
                dateEnvoiPlanifie: dateEnvoi ? new Date(dateEnvoi).toISOString() : null,
            };
            const { data } = await axios.post(
                `${API_BASE_URL}/api/v1/marketing/campaigns`,
                payload,
                { withCredentials: true }
            );
            toast.success('Campagne créée avec succès');
            onClose();
            onCreated?.();
            navigate(`/marketing/campaigns/${data.id}`);
        } catch {
            toast.error('Erreur lors de la création');
        } finally {
            setSaving(false);
        }
    };

    // ── Segment label for summary ─────────────────────────────────────────────
    const segmentLabel = audienceMode === 'manuel'
        ? 'Sélection manuelle'
        : (SEGMENT_DEFS.find(s => s.key === selectedSegment)?.label ?? selectedSegment);

    return (
        <div className="ccm-overlay" onClick={onClose}>
            <div className="ccm-modal" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="ccm-header">
                    <span className="ccm-title">Nouvelle campagne</span>
                    <button className="ccm-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Stepper */}
                <div className="ccm-stepper">
                    {STEPS.map((label, i) => (
                        <div key={i} className={`ccm-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
                            <div className="ccm-step-num">
                                {i < step ? <FontAwesomeIcon icon={faCheck} /> : i + 1}
                            </div>
                            <span>{label}</span>
                            {i < STEPS.length - 1 && <div className="ccm-step-line" />}
                        </div>
                    ))}
                </div>

                {/* Body */}
                <div className="ccm-body">

                    {/* ── Step 0: Paramètres ── */}
                    {step === 0 && (
                        <>
                            <div className="ccm-field">
                                <label>Nom de la campagne *</label>
                                <input
                                    className="ccm-input"
                                    value={nom}
                                    onChange={e => setNom(e.target.value)}
                                    placeholder="Ex: Promotion Ramadan 2025"
                                    autoFocus
                                />
                            </div>

                            <div className="ccm-field">
                                <label>Canal *</label>
                                <div className="ccm-canal-grid">
                                    {CANAL_OPTIONS.map(o => (
                                        <button
                                            key={o.value}
                                            className={`ccm-canal-card ${canal === o.value ? 'active' : ''}`}
                                            onClick={() => { setCanal(o.value); setTemplateId(''); }}
                                            type="button"
                                        >
                                            <FontAwesomeIcon icon={o.icon} className="ccm-canal-icon" />
                                            <strong>{o.label}</strong>
                                            <span>{o.desc}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {canal === 'email' && (
                                <div className="ccm-field">
                                    <label>Sujet de l'email</label>
                                    <input
                                        className="ccm-input"
                                        value={sujet}
                                        onChange={e => setSujet(e.target.value)}
                                        placeholder="Ex: Offre spéciale pour vous !"
                                    />
                                </div>
                            )}

                            <div className="ccm-field">
                                <label>Template ({filteredTemplates.length} disponibles)</label>
                                <select
                                    className="ccm-select"
                                    value={templateId}
                                    onChange={e => setTemplateId(e.target.value)}
                                >
                                    <option value="">— Aucun template (contenu libre) —</option>
                                    {filteredTemplates.map(t => (
                                        <option key={t.id} value={t.id}>
                                            {t.nom}{t.isSystem ? ' (système)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {selectedTemplate?.textContent && (
                                <div className="ccm-preview-snippet">
                                    <div className="ccm-preview-label">Aperçu du template</div>
                                    <p>{selectedTemplate.textContent.slice(0, 200)}{selectedTemplate.textContent.length > 200 ? '…' : ''}</p>
                                </div>
                            )}
                        </>
                    )}

                    {/* ── Step 1: Audience ── */}
                    {step === 1 && (
                        <>
                            {/* Mode toggle */}
                            <div className="ccm-audience-tabs">
                                <button
                                    className={`ccm-audience-tab ${audienceMode === 'segment' ? 'active' : ''}`}
                                    onClick={() => setAudienceMode('segment')}
                                >
                                    Par segment
                                </button>
                                <button
                                    className={`ccm-audience-tab ${audienceMode === 'manuel' ? 'active' : ''}`}
                                    onClick={() => setAudienceMode('manuel')}
                                >
                                    Sélection manuelle
                                </button>
                            </div>

                            {/* Option A — Segment */}
                            {audienceMode === 'segment' && (
                                <div className="ccm-segment-grid">
                                    {SEGMENT_DEFS.map(seg => {
                                        const count = segmentCounts ? segmentCounts[seg.key] : null;
                                        return (
                                            <button
                                                key={seg.key}
                                                className={`ccm-segment-card ${selectedSegment === seg.key ? 'active' : ''}`}
                                                onClick={() => setSelectedSegment(seg.key)}
                                                type="button"
                                            >
                                                <div className="ccm-segment-icon" style={{ background: seg.color + '18', color: seg.color }}>
                                                    <FontAwesomeIcon icon={seg.icon} />
                                                </div>
                                                <div className="ccm-segment-count">
                                                    {count === null ? '—' : count.toLocaleString()}
                                                    <span> patients</span>
                                                </div>
                                                <div className="ccm-segment-name">{seg.label}</div>
                                                <div className="ccm-segment-desc">{seg.desc}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Option B — Manuel */}
                            {audienceMode === 'manuel' && (
                                <div className="ccm-manual">
                                    <div className="ccm-manual-toolbar">
                                        <div className="ccm-search-wrap">
                                            <FontAwesomeIcon icon={faSearch} className="ccm-search-icon" />
                                            <input
                                                className="ccm-search-input"
                                                placeholder="Rechercher par nom ou téléphone..."
                                                value={searchQ}
                                                onChange={e => setSearchQ(e.target.value)}
                                                autoFocus
                                            />
                                        </div>
                                        <button className="ccm-sel-btn" onClick={selectAll}>Tout sélectionner</button>
                                        <button className="ccm-sel-btn" onClick={deselectAll}>Tout désélectionner</button>
                                    </div>

                                    <div className="ccm-patient-list">
                                        {patientsLoading ? (
                                            <div className="ccm-patient-empty">Chargement...</div>
                                        ) : patients.length === 0 ? (
                                            <div className="ccm-patient-empty">Aucun patient trouvé</div>
                                        ) : patients.map(p => {
                                            const checked = selectedPatientIds.includes(p.id);
                                            const lastDate = p.lastOrderDate
                                                ? new Date(p.lastOrderDate).toLocaleDateString('fr-FR')
                                                : '—';
                                            return (
                                                <label key={p.id} className={`ccm-patient-row ${checked ? 'selected' : ''}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => togglePatient(p.id)}
                                                    />
                                                    <div className="ccm-patient-info">
                                                        <span className="ccm-patient-name">{p.firstName} {p.lastName}</span>
                                                        <span className="ccm-patient-meta">{p.phoneNumber || '—'} · Dernier achat : {lastDate}</span>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>

                                    <div className="ccm-manual-footer">
                                        <strong>{selectedPatientIds.length}</strong> patient{selectedPatientIds.length !== 1 ? 's' : ''} sélectionné{selectedPatientIds.length !== 1 ? 's' : ''}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* ── Step 2: Confirmation ── */}
                    {step === 2 && (
                        <>
                            <div className="ccm-summary">
                                <div className="ccm-summary-row"><span>Nom</span><strong>{nom}</strong></div>
                                <div className="ccm-summary-row">
                                    <span>Canal</span>
                                    <strong>{CANAL_OPTIONS.find(o => o.value === canal)?.label}</strong>
                                </div>
                                {sujet && <div className="ccm-summary-row"><span>Sujet</span><strong>{sujet}</strong></div>}
                                <div className="ccm-summary-row">
                                    <span>Template</span>
                                    <strong>{selectedTemplate ? selectedTemplate.nom : '— Contenu libre —'}</strong>
                                </div>
                                <div className="ccm-summary-row">
                                    <span>Segment</span>
                                    <strong>{segmentLabel}</strong>
                                </div>
                                <div className="ccm-summary-row">
                                    <span>Destinataires</span>
                                    <strong>{audienceCount.toLocaleString()} patients</strong>
                                </div>
                            </div>

                            <div className="ccm-field" style={{ marginTop: 16 }}>
                                <label>Date d'envoi planifiée (optionnel)</label>
                                <input
                                    className="ccm-input"
                                    type="datetime-local"
                                    value={dateEnvoi}
                                    onChange={e => setDateEnvoi(e.target.value)}
                                />
                            </div>

                            {canal === 'email' && (
                                <div className="ccm-field">
                                    <label>Offre / promotion (optionnel)</label>
                                    <input
                                        className="ccm-input"
                                        value={offre}
                                        onChange={e => setOffre(e.target.value)}
                                        placeholder="Ex: -20% sur tous les compléments alimentaires"
                                    />
                                    <span className="ccm-hint">Remplace la variable <code>{'{offre}'}</code> dans le template</span>
                                </div>
                            )}

                            <div className="ccm-field">
                                <label>Contenu personnalisé (optionnel)</label>
                                <textarea
                                    className="ccm-textarea"
                                    value={contenu}
                                    onChange={e => setContenu(e.target.value)}
                                    rows={3}
                                    placeholder="Message personnalisé (remplace le template si renseigné)"
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="ccm-footer">
                    {step > 0 && (
                        <button className="ccm-btn ccm-btn--ghost" onClick={() => setStep(s => s - 1)}>
                            <FontAwesomeIcon icon={faChevronLeft} /> Précédent
                        </button>
                    )}
                    <div style={{ flex: 1 }} />
                    <button className="ccm-btn ccm-btn--ghost" onClick={onClose}>Annuler</button>
                    {step < STEPS.length - 1 ? (
                        <button
                            className="ccm-btn ccm-btn--primary"
                            onClick={() => setStep(s => s + 1)}
                            disabled={!canNext()}
                        >
                            Suivant <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    ) : (
                        <button className="ccm-btn ccm-btn--primary" onClick={handleSubmit} disabled={saving}>
                            <FontAwesomeIcon icon={faPaperPlane} />
                            {saving ? 'Création...' : 'Créer la campagne'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
